import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CommandDialog } from '@/components/ui/command';

/**
 * Workspace-scoped ⌘K command palette host.
 *
 * Mounted by `WorkspaceShell` so every workspace-scoped route shares
 * one palette instance. The provider only holds open/close state; the
 * actual `<CommandPalette>` body (with its four background queries —
 * tasks / notes / projects / members) is lazy-imported and only
 * mounts while the dialog is open. That way landing on a deep page
 * doesn't fire the four list queries until the user actually presses
 * ⌘K.
 *
 * The global ⌘K / Ctrl+K shortcut listens at the window level. We do
 * NOT suppress it inside text inputs — Linear / Notion / Raycast all
 * keep ⌘K live everywhere, and users expect "search from anywhere"
 * to be unblockable. Browser-level ⌘K (URL-bar focus on some
 * browsers) is preempted by `event.preventDefault()`.
 */

interface CommandPaletteContextValue {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/**
 * Hook to trigger the palette from any descendant. Today's only
 * caller is the topbar Search button (`BoardTopBarActions.tsx`); the
 * global ⌘K shortcut goes straight through the provider's listener
 * and doesn't need this.
 *
 * Returns a no-op `open` if called outside a provider so non-
 * workspace surfaces (auth pages, onboarding) don't crash if the
 * Search button ever migrates up the tree.
 */
export const useCommandPalette = (): CommandPaletteContextValue => {
  const ctx = useContext(CommandPaletteContext);
  if (ctx) return ctx;
  return {
    open: () => {},
    close: () => {},
    isOpen: false,
  };
};

/* Lazy — keeps the four background queries off the workspace shell's
   initial render path. The CommandPalette chunk loads on first
   open; subsequent opens are instant. */
const CommandPalette = lazy(() =>
  import('@/components/CommandPalette').then((m) => ({ default: m.CommandPalette })),
);

export interface CommandPaletteProviderProps {
  workspaceSlug: string;
  children: ReactNode;
}

export const CommandPaletteProvider = ({
  workspaceSlug,
  children,
}: CommandPaletteProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Global ⌘K / Ctrl+K. preventDefault before the browser's URL-bar
  // shortcut on Firefox / Edge can intercept it. Event runs on the
  // capture phase via the default `addEventListener` ordering — works
  // even when focus is in a text input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandDialog open={isOpen} onOpenChange={setIsOpen}>
        {/*
          Body only mounts while the dialog is open — react-query
          hooks inside `<CommandPalette>` only fire on first open,
          and any list updates (e.g. after creating a task) hydrate
          via the same caches the rest of the app uses.
        */}
        {isOpen ? (
          <Suspense
            fallback={
              <div className="text-foreground-faint px-3 py-6 text-center text-sm">
                Loading…
              </div>
            }
          >
            <CommandPalette workspaceSlug={workspaceSlug} onClose={close} />
          </Suspense>
        ) : null}
      </CommandDialog>
    </CommandPaletteContext.Provider>
  );
};
